import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { useAuthModal } from '../hooks/useAuthModal';
import { AuthRequiredModal } from './AuthRequiredModal';
import { LoginModal } from './LoginModal';
import { SimpleCheckboxIcon } from './CustomIcons';
import { savePendingAction } from '../utils/authRedirectState';

interface CompletionStatusButtonProps {
  problemId: string;
  onCompletionChange?: (isCompleted: boolean) => void;
  className?: string;
}

export const CompletionStatusButton: React.FC<CompletionStatusButtonProps> = ({
  problemId,
  onCompletionChange,
  className = '',
}) => {
  const { isAuthenticated } = useAuth();
  const {
    showAuthRequiredModal,
    showLoginModal,
    requireAuth,
    handleOk,
    handleLoginSuccess,
    handleClose: handleAuthClose
  } = useAuthModal();
  const [isCompleted, setIsCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load initial completion status
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const loadCompletionStatus = async () => {
      try {
        const response = await apiService.getCompletionStatus(problemId);
        const completionStatus = response.data?.completed?.isCompleted ?? false;
        setIsCompleted(completionStatus);

        // Notify parent component of initial completion status
        if (onCompletionChange) {
          onCompletionChange(completionStatus);
        }
      } catch (error) {
        console.error('Failed to load completion status:', error);
        // Don't show error for initial load
      }
    };

    loadCompletionStatus();
  }, [problemId, isAuthenticated, onCompletionChange]);

  // Listen for auth redirect completion to resume pending actions
  useEffect(() => {
    const handleAuthRedirect = async (e: Event) => {
      const customEvent = e as CustomEvent;
      const { pendingAction } = customEvent.detail;
      if (pendingAction?.type === 'toggle_completion' && pendingAction.data.problemId === problemId) {
        // Resume toggling completion for this specific problem
        await performToggle();
      }
    };

    window.addEventListener('auth-redirect-complete', handleAuthRedirect);
    return () => window.removeEventListener('auth-redirect-complete', handleAuthRedirect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemId]);

  const handleToggle = async () => {
    // If not authenticated, save action before redirect
    if (!isAuthenticated) {
      savePendingAction({
        type: 'toggle_completion',
        data: { problemId }
      });
      // Trigger auth flow (action will resume after redirect)
      requireAuth(() => {});
      return;
    }

    // Already authenticated, proceed immediately
    await performToggle();
  };

  const performToggle = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.toggleCompletionStatus(problemId);
      const newCompletionStatus = response.data.isCompleted;
      setIsCompleted(newCompletionStatus);

      // Notify parent component of completion status change
      if (onCompletionChange) {
        onCompletionChange(newCompletionStatus);
      }

      // Show success message briefly
      if (response.message) {
        console.log(response.message);
      }
    } catch (error: any) {
      console.error('Failed to toggle completion status:', error);
      const errorMessage = error.message?.includes('Invalid token') || error.message?.includes('401')
        ? 'Please sign in to track completed problems.'
        : 'Failed to update completion status. Please try again.';
      setError(errorMessage);

      // Clear error after 3 seconds
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`completion-status-container ${className}`}>
      <button
        onClick={handleToggle}
        disabled={isLoading}
        className={`completion-status-button ${isCompleted ? 'completed' : 'not-completed'} ${isLoading ? 'loading' : ''}`}
        title={
          !isAuthenticated 
            ? 'Sign in to track completed problems' 
            : isCompleted 
              ? 'Mark as not completed' 
              : 'Mark as completed'
        }
      >
        {isLoading ? (
          <div className="completion-status-spinner">⟳</div>
        ) : (
          <div className="completion-status-icon">
            {isCompleted ? (
              <SimpleCheckboxIcon size={18} color="#28a745" />
            ) : (
              <div style={{ width: 18, height: 18, border: '2px solid #6c757d', borderRadius: '4px', backgroundColor: 'white' }}></div>
            )}
          </div>
        )}
      </button>
      
      {error && (
        <div className="completion-status-error">
          {error}
        </div>
      )}

      <AuthRequiredModal
        isOpen={showAuthRequiredModal}
        onClose={handleAuthClose}
        onOk={handleOk}
      />

      <LoginModal
        isOpen={showLoginModal}
        onClose={handleAuthClose}
        onSuccess={handleLoginSuccess}
      />
    </div>
  );
};